import { faColumns3 } from '@fortawesome/pro-regular-svg-icons'
import { Modify } from '@teinor/erp'
import { useCallback, useState } from 'react'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import { getOnlyDefaultColumns, getTableSelectedColumns, SelectedColumnsAndFilters } from '../../../utils/hooks/useConfigColumns'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { changeEmployeeParams } from '../../../utils/reducers/reduxDispatch'
import { ButtonSecondary } from '../../Button'
import { ModalOpenedTy } from '../../Modal/types'
import TwinIcon, { TwinIconToolTip } from '../../TwinIcon'
import TwinTrans from '../../TwinTrans'
import { ModalComponentPropsWithoutSubmit } from '../types'
import { ConfigColumnsColumn, ModalConfigColumnsBaseProps } from './types'
import ModalTabs from '../ModalTabs'
import { ComponentTabObject } from '../../../utils/hooks/useTabLogic'
import ModalConfigColumns from './ModalConfigColumns'
import ModalConfigColumnsFilters from './ModalConfigColumnsFilters'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import { ButtonsLoadFiltersStateFull } from '../../Button/ButtonLoadFilter'
import ModalConfigColumnsOrder from './ModalConfigColumnsOrder'
import { TableConfiguration } from '../../TwinTable/types'
import { faRotateLeft } from '@fortawesome/pro-light-svg-icons'
import { flushSync } from 'react-dom'
import { getSingleEmployeeParam } from '../../../utils/reducers/getters'
import './modalConfigColumnsTabs.sass'

interface ModalConfigColumnsTabsProps extends ModalConfigColumnsTabsLogicProps {
    haveFiltersColumn?: boolean
}

const ModalConfigColumnsTabs: React.FC<ModalConfigColumnsTabsProps> = ({setOpened, haveFiltersColumn = true, ...rest }) => {
    const { t } = useTwinTranslation()
    const logic = useModalConfigColumnsTabsLogic({setOpened, ...rest})
    const tabs: ComponentTabObject = {
        'main': { component: ModalConfigColumns, text: t('configColumns', 'Configurar columnas') },
    }
    if (haveFiltersColumn) {
        tabs['filters'] = { component: ModalConfigColumnsFilters, text: t('configFilters', 'Configurar filtros') }
        tabs['order'] = { component: ModalConfigColumnsOrder, text: t('configOrder', 'Configurar orden') }
    }
    return (
        <ModalTabs tabs={tabs} setOpened={setOpened} onSubmit={() => setOpened(null)} extraComponentData={logic} userPermissions={{ n: 4, permission: 'delete' }} className='modal_config_columns_tabs'></ModalTabs>
    )
}

interface ModalConfigColumnsTabsLogicProps extends ModalComponentPropsWithoutSubmit {
    setSelectedColumnsAndFilters: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
    selectedColumnsAndFilters: SelectedColumnsAndFilters
    columns: ConfigColumnsColumn<any>[]
    name: string
    setTableConfiguration: React.Dispatch<React.SetStateAction<TableConfiguration>>
}

const useModalConfigColumnsTabsLogic = ({ selectedColumnsAndFilters, columns, setSelectedColumnsAndFilters, setOpened, name, setTableConfiguration }: ModalConfigColumnsTabsLogicProps): ModalConfigColumnsBaseProps => {
    const [tmpColumns, setTmpColumns] = useState(selectedColumnsAndFilters)
    
    const updateFilterOnTable = useCallback(async (columns: SelectedColumnsAndFilters, id: number) => {
        delete columns.id
        delete columns.name
        const myValue = JSON.stringify(columns)
        await twinFetchPostJSON('/api/app/config/filter/updateFilter', { id, value: myValue })
    }, [])

    const updateEmployeeParams = useCallback(async (columns: SelectedColumnsAndFilters) => {
        delete columns.id
        delete columns.name
        const myName = 'table_' + name
        const myValue = JSON.stringify(columns)
        await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: myValue })
        changeEmployeeParams({ [myName]: myValue })
    }, [name])

    const updateParamsDecider = useCallback(async (columns: SelectedColumnsAndFilters) => {
        if (tmpColumns.id && String(tmpColumns.id) !== '0') {
            await updateFilterOnTable(columns, tmpColumns.id)
        } else {
            await updateEmployeeParams(columns)
        }
    }, [tmpColumns.id, updateFilterOnTable, updateEmployeeParams])

    const onSave = useCallback(() => {
        if (!Object.keys(tmpColumns.selectedColumns).length) {
            const defaultCols = getOnlyDefaultColumns(columns)
            const myColumns = { ...defaultCols, filters: tmpColumns.filters, id: tmpColumns.id, name: tmpColumns.name }
            setSelectedColumnsAndFilters(myColumns)
            updateParamsDecider(myColumns)
        } else {
            setSelectedColumnsAndFilters({ ...tmpColumns })
            updateParamsDecider({ ...tmpColumns })
        }
        setTableConfiguration((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            copy.offset = 0
            return copy
        })
        setOpened(null)
        return false
    }, [tmpColumns, setSelectedColumnsAndFilters, setOpened, columns, updateParamsDecider, setTableConfiguration])

    return {tmpColumns, setTmpColumns, onSave, columns, selectedColumnsAndFilters, name}
}


type ButtonConfigColumnsProps = Modify<ModalConfigColumnsTabsProps, ButtonConfigColumnsWithModalLogicProps & {
    setOpened?: never
    hideLoadFiltersButton?: boolean
    children?: React.ReactNode
    getTableDataFromStart: (resetFilter: boolean) => void
}>

export const ButtonConfigColumnsWithModalBase: React.FC<ButtonConfigColumnsProps> = ({ selectedColumnsAndFilters, setSelectedColumnsAndFilters, hideLoadFiltersButton, setTableConfiguration, children, ...rest }) => {
    const [opened, setOpened] = useState<ModalOpenedTy>(null)
    const defaultValues = getTableSelectedColumns(rest.name, rest.columns)
    const { onChangeFilter } = useButtonConfigColumnsWithModalLogic({ setSelectedColumnsAndFilters, setTableConfiguration, name: rest.name })
    return (
        <div className='button_config_columns_modal flex items-center flex-wrap gap-10'>
            {children}
            {hideLoadFiltersButton ? null : <ButtonsLoadFiltersStateFull allowCreate={false} selectedFilters={selectedColumnsAndFilters} onChangeFilter={(json) => onChangeFilter(json)} tableName={rest.name} defaultValues={defaultValues} className='load_button_filter'/>}
            <ButtonSecondary onClick={() => setOpened(true)} className='h-34'>
                <TwinIcon icon={faColumns3} />
                <span className='ml-10 load_columns_button_text'><TwinTrans transKey='configColumns'>Configurar columnas</TwinTrans></span>
            </ButtonSecondary>
            {opened && <ModalConfigColumnsTabs {...rest} setOpened={setOpened} selectedColumnsAndFilters={selectedColumnsAndFilters} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} setTableConfiguration={setTableConfiguration}/>}
        </div>
    )
}

export const ButtonConfigColumnsWithModal: React.FC<ButtonConfigColumnsProps> = ({ ...rest}) => {
    return (
        <ButtonConfigColumnsWithModalBase {...rest}>
            <ResetButtonConfigColumns {...rest} />
        </ButtonConfigColumnsWithModalBase>
    )
}

interface ButtonConfigColumnsWithModalLogicProps {
    name: string
    setSelectedColumnsAndFilters: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
    setTableConfiguration: React.Dispatch<React.SetStateAction<TableConfiguration>>
}

const useButtonConfigColumnsWithModalLogic = ({ setSelectedColumnsAndFilters, setTableConfiguration }: ButtonConfigColumnsWithModalLogicProps) => {
    
    const onChangeFilter = useCallback((json: TwinDictionary) => {
        setSelectedColumnsAndFilters({ filters: json?.value?.filters || {}, selectedColumns: json?.value?.selectedColumns || {}, id: json.id, name: json.name, order: json?.value?.order || {} })
        setTableConfiguration((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            copy.offset = 0
            return copy
        })
    }, [setSelectedColumnsAndFilters, setTableConfiguration])

    return { onChangeFilter }
}

interface ResetButtonConfigColumnsProps extends ResetButtonConfigColumnsLogicProps {
    selectedColumnsAndFilters: SelectedColumnsAndFilters
}

export const ResetButtonConfigColumns: React.FC<ResetButtonConfigColumnsProps> = ({selectedColumnsAndFilters, setSelectedColumnsAndFilters, setTableConfiguration, ...rest}) => {
    const { resetFilter } = ResetuseButtonConfigColumnsLogic({ setSelectedColumnsAndFilters, setTableConfiguration, ...rest })
    const { t } = useTwinTranslation()

    const filtersLength = Object.keys(selectedColumnsAndFilters?.filters || {}).length
    const generalHeadersLength = Object.keys(selectedColumnsAndFilters?.generalHeaders || {}).length
    const orderLength = Object.keys(selectedColumnsAndFilters?.order || {}).length
    const isCustomFilter = selectedColumnsAndFilters.id && String(selectedColumnsAndFilters.id) !== '0'
    const name = isCustomFilter && selectedColumnsAndFilters.name ? selectedColumnsAndFilters.name : t('byDefault', 'Por defecto')
    const haveFilters1 = filtersLength > 0 || isCustomFilter
    const haveFilters2 = !filtersLength && !isCustomFilter && (orderLength || selectedColumnsAndFilters.generalSearchbar || generalHeadersLength)
    if (!haveFilters1 && !haveFilters2) {
        return null
    }
    return (
        <div className='reset_button_config_columns'>
            {haveFilters1 ?
                <div className='flex items-center mr-15 reset_button_filters'>
                    <span className='medium16 reset_button_filters_name'><TwinTrans transKey='configuration'>Configuración</TwinTrans>: {name} ({filtersLength})</span>
                    <TwinIconToolTip classNameIcon='flex items-end text-green-21 ml-10 reset_button_config_icon' message={t('deleteFilter', 'Borrar filtro')} id='resetFilter' icon={faRotateLeft} onClick={resetFilter} />
                </div>
                : null}
            {haveFilters2 ? <div className='flex items-center text-green-21 regular16 cursor-pointer' onClick={resetFilter}>
                <span className='mr-10 reset_button_config_name'>Reiniciar</span>
                <TwinIcon className='reset_button_config_icon' icon={faRotateLeft} />
            </div> : null}
        </div>
    )
}

interface ResetButtonConfigColumnsLogicProps {
    setSelectedColumnsAndFilters: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
    setTableConfiguration: React.Dispatch<React.SetStateAction<TableConfiguration>>
    defaultValues?: TwinDictionary
    name: string
    getTableDataFromStart: (resetFilter: boolean) => void
}

const ResetuseButtonConfigColumnsLogic = ({ name, setSelectedColumnsAndFilters, setTableConfiguration, defaultValues, getTableDataFromStart }: ResetButtonConfigColumnsLogicProps) => {

    const myDefaultParsed = JSON.stringify(defaultValues)

    const resetFilter = useCallback(async () => {
        const myName = 'table_' + name
        await flushSync(async () => {
            let data: TwinDictionary = {}
            let isCustomFilter = false
            await setSelectedColumnsAndFilters((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                if (copyOld.id && String(copyOld.id) !== '0') {
                    isCustomFilter = true
                    const table = getSingleEmployeeParam(myName) || '{}'
                    if (table) {
                        const myTableParsed = JSON.parse(table)
                        if (myTableParsed.selectedColumns) {
                            copyOld.selectedColumns = myTableParsed.selectedColumns
                        } else {
                            copyOld.selectedColumns = JSON.parse(myDefaultParsed).selectedColumns
                        }
                    }
                }
                data = { ...copyOld, filters: {}, order: {}, generalSearchbar: '', name: '', id: '0', generalHeaders: {} }
                return { ...copyOld, filters: {}, order: {}, generalSearchbar: '', name: '', id: '0', generalHeaders: {} }
            })
            getTableDataFromStart(true)
            setTableConfiguration((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return { ...copy, filtersFieldsSearch: {}, singleFieldsSearch: {}, order: {}, offset: 0, generalSearchbar: '', generalHeaders: {} }
            })
            if (!isCustomFilter && getSingleEmployeeParam('saveTableDefaultFilterWhenChanges') === 'on') {
                delete data.id
                delete data.name
                const dataStr = JSON.stringify(data)
                await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: dataStr })
                await changeEmployeeParams({ [myName]: dataStr })
            }
        })
    }, [name, setSelectedColumnsAndFilters, setTableConfiguration, myDefaultParsed, getTableDataFromStart])

    return { resetFilter }
}

export default ModalConfigColumnsTabs